import React from 'react';
import * as Yup from 'yup';
import { useForm, useField } from '@hum/common/src/modules/form';
import { ChannelType, CompanyType, UserIdentity } from '@hum/types';
import { Modal } from '@hum/design-system';
import { Button, Scrollable } from '@hum/ui-library';
import { TextInputField } from '@hum/legacy-ui';
import { createCompanyUser, updateCompanyUser, useApiMutation } from '@hum/api';
import { UserRole } from '@hum/api/src/types/User.model';
import { toast } from '@hum/common/src/modules/toast';

const initialValues = {
  firstName: '',
  lastName: '',
  email: '',
  position: '',
  phone: '',
};

// prettier-ignore
const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  email: Yup.string().email('Please enter a valid email').required('Required'),
  position: Yup.string().required('Required'),
  phone: Yup.string().nullable(),
})

const getUserTypeMapping = (companyType: string) => {
  const mapping = {
    [CompanyType.Company]: {
      userRole: UserRole.COMPANY,
      organizationType: CompanyType.Company,
    },
    [CompanyType.Admin]: {
      userRole: UserRole.ADMIN,
      organizationType: CompanyType.Admin,
    },
    [CompanyType.SyndicationInvestor]: {
      userRole: UserRole.FUND,
      organizationType: CompanyType.Fund,
    },
  };

  return mapping[companyType] || mapping[CompanyType.Company];
};

const useUserModal = ({
  companyId,
  closeModal,
  isUpdate,
  currentUser,
  companyType,
}: {
  companyId: string;
  closeModal: () => void;
  isUpdate: boolean;
  currentUser: UserIdentity;
  companyType: string;
}) => {
  const { mutate: createUser } = useApiMutation(createCompanyUser, {
    onError: (error: any) => {
      const errorMessage = error.response?.data?.detail;
      toast.error(errorMessage || 'Failed to invite user');
    },
  });

  const { mutate: updateUser } = useApiMutation(updateCompanyUser);

  const form = useForm({
    initialValues: isUpdate ? currentUser : initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (user: Partial<UserIdentity>, { resetForm }) => {
      const cleanUser = Object.keys(initialValues).reduce(
        (picked, key) => ({ ...picked, [key]: user && user[key] }),
        {}
      ) as UserIdentity;

      const userId = user.id!;
      if (!isUpdate) {
        cleanUser.channel =
          companyType == CompanyType.Company
            ? ChannelType.icm_company_invitation
            : ChannelType.icm_investor_invitation;

        if (companyType === CompanyType.Admin) {
          toast.error('Admin users are not allowed to be created');
          return;
        }

        createUser({
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          phone: user.phone,
          position: user.position,
          organizationId: companyId,
          ...getUserTypeMapping(companyType),
        });
      } else {
        updateUser({
          id: userId,
          firstName: cleanUser.firstName,
          lastName: cleanUser.lastName,
          phone: cleanUser.phone,
          position: cleanUser.position,
        });
      }
      resetForm();
      closeModal();
    },
  });

  const fields = {
    firstName: useField('firstName', 'text', form).input,
    lastName: useField('lastName', 'text', form).input,
    email: useField('email', 'text', form).input,
    phone: useField('phone', 'text', form).input,
    position: useField('position', 'text', form).input,
  };

  return {
    form,
    fields,
  };
};

export const AddUser = ({
  isVisible,
  companyId,
  companyType,
  closeModal,
  currentUser,
}: {
  isVisible: boolean;
  companyId: string;
  companyType: string;
  closeModal: () => void;
  currentUser: UserIdentity;
}) => {
  const isUpdate = !!currentUser?.id;

  const { form, fields } = useUserModal({
    companyId,
    closeModal,
    isUpdate,
    currentUser,
    companyType,
  });

  return (
    <Modal
      side
      wide
      visible={isVisible}
      onClose={closeModal}
      header={`${isUpdate ? 'Update' : 'Add'} user`}
      scrollable
      footer={
        <>
          <Button
            onClick={() => {
              form.resetForm();
              closeModal();
            }}
          >
            Cancel
          </Button>
          <Button submit onClick={form.handleSubmit}>
            {isUpdate ? 'Update' : 'Create'}
          </Button>
        </>
      }
    >
      <Scrollable className="mb-0">
        <TextInputField
          label="First Name"
          inputTestId="adduser:firstName"
          {...fields.firstName}
        />
        <TextInputField
          label="Last Name"
          inputTestId="adduser:lastName"
          {...fields.lastName}
        />
        {!isUpdate && (
          <TextInputField
            label="Email"
            inputTestId="adduser:email"
            {...fields.email}
          />
        )}
        <TextInputField
          label="Phone"
          inputTestId="adduser:phone"
          {...fields.phone}
        />
        <TextInputField
          label="Position"
          inputTestId="adduser:position"
          {...fields.position}
        />
      </Scrollable>
    </Modal>
  );
};
